import "./NightHeroLayer.css";
import { CLOCK_BASELINE_Y, CLOCK_GLYPH_PATHS } from "../../config/clockGlyphPaths";

export type NightHeroBackgroundMode = "white" | "night";

export interface NightHeroLayerProps {
  time: string;
  backgroundMode?: NightHeroBackgroundMode;
  wallpaperSrc?: string;
  evaSrc?: string;
  evaExpandedSrc?: string;
  evaAlertSrc?: string;
  hasPrompt?: boolean;
  isCardExpanded?: boolean;
  className?: string;
}

export function NightHeroLayer({
  time,
  backgroundMode = "white",
  wallpaperSrc,
  evaSrc,
  evaExpandedSrc,
  evaAlertSrc,
  hasPrompt = false,
  isCardExpanded = false,
  className
}: NightHeroLayerProps) {
  const classes = [
    "night-hero-layer",
    `night-hero-layer--${backgroundMode}`,
    hasPrompt ? "night-hero-layer--has-prompt" : "",
    isCardExpanded ? "night-hero-layer--card-expanded" : "",
    className
  ]
    .filter(Boolean)
    .join(" ");
  const [timeHours = "", timeMinutes = ""] = time.split(":");
  const clockGlyphs = [
    ...layoutClockGlyphs(timeHours, 350, "end", "hours"),
    ...layoutClockGlyphs(":", 382, "middle", "separator"),
    ...layoutClockGlyphs(timeMinutes, 414, "start", "minutes")
  ];

  return (
    <section className={classes} aria-label="夜间 AI 桌面背景与时间">
      {wallpaperSrc ? (
        <img className="night-hero-layer__wallpaper" src={wallpaperSrc} alt="" />
      ) : null}

      <time
        className="night-hero-layer__time"
        dateTime={time}
        data-clock-value={time}
        aria-label={`当前时间 ${time}`}
      >
        <svg
          className="night-hero-layer__time-svg"
          viewBox="0 0 796 365"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="night-time-fill" x1="397.996" y1="-319.799" x2="397.996" y2="399.201" gradientUnits="userSpaceOnUse">
              <stop stopColor="white" stopOpacity="0.5" />
              <stop offset="1" stopColor="white" stopOpacity="0" />
            </linearGradient>
            <filter id="night-time-inner-glass" x="-100" y="-100" width="996" height="565" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
              <feOffset />
              <feGaussianBlur stdDeviation="7" />
              <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
              <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.7 0" />
              <feBlend mode="normal" in2="shape" result="effect1_innerShadow" />
            </filter>
            <filter id="night-time-outline-ring" x="-100" y="-100" width="996" height="565" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feMorphology in="SourceAlpha" operator="dilate" radius="1.35" result="expanded" />
              <feMorphology in="SourceAlpha" operator="erode" radius="1" result="contracted" />
              <feComposite in="expanded" in2="contracted" operator="out" result="edgeBand" />
              <feGaussianBlur in="edgeBand" stdDeviation="0.15" result="softEdgeBand" />
              <feFlood floodColor="white" floodOpacity="0.74" result="edgeColor" />
              <feComposite in="edgeColor" in2="softEdgeBand" operator="in" result="edgeLine" />
              <feGaussianBlur in="edgeBand" stdDeviation="1.2" result="edgeGlowAlpha" />
              <feFlood floodColor="white" floodOpacity="0.18" result="edgeGlowColor" />
              <feComposite in="edgeGlowColor" in2="edgeGlowAlpha" operator="in" result="edgeGlow" />
              <feMerge>
                <feMergeNode in="edgeGlow" />
                <feMergeNode in="edgeLine" />
              </feMerge>
            </filter>
          </defs>
          <g className="night-hero-layer__time-outline-glyphs" filter="url(#night-time-outline-ring)">
            {clockGlyphs.map((glyph) => (
              <path
                key={`${glyph.id}-outline`}
                className="night-hero-layer__time-outline"
                d={glyph.d}
                transform={`translate(${glyph.x} ${CLOCK_BASELINE_Y})`}
              />
            ))}
          </g>
          <g className="night-hero-layer__time-glyphs" filter="url(#night-time-inner-glass)">
            {clockGlyphs.map((glyph) => (
              <path
                key={`${glyph.id}-fill`}
                className="night-hero-layer__time-fill"
                d={glyph.d}
                transform={`translate(${glyph.x} ${CLOCK_BASELINE_Y})`}
              />
            ))}
          </g>
        </svg>
        <span className="night-hero-layer__time-label">{time}</span>
      </time>

      <div className="night-hero-layer__eva" role="img" aria-label="EVA 助手">
        <span className="night-hero-layer__eva-shadow" aria-hidden="true" />
        <span className="night-hero-layer__eva-alert-motion" aria-hidden="true">
          {evaAlertSrc ? (
            <img className="night-hero-layer__eva-alert" src={evaAlertSrc} alt="" draggable={false} />
          ) : null}
        </span>
        <span className="night-hero-layer__eva-head-motion">
          <span className="night-hero-layer__eva-head-breath">
            {evaSrc ? (
              <>
                <img className="night-hero-layer__eva-head night-hero-layer__eva-head--front" src={evaSrc} alt="" draggable={false} />
                <img className="night-hero-layer__eva-head night-hero-layer__eva-head--expanded" src={evaExpandedSrc ?? evaSrc} alt="" draggable={false} />
              </>
            ) : (
              <span className="night-hero-layer__eva-head night-hero-layer__eva-head--fallback" />
            )}
          </span>
        </span>
      </div>
    </section>
  );
}

type ClockAnchor = "start" | "middle" | "end";

interface ClockGlyphLayout {
  id: string;
  d: string;
  x: number;
}

function layoutClockGlyphs(
  value: string,
  anchorX: number,
  anchor: ClockAnchor,
  idPrefix: string
): ClockGlyphLayout[] {
  const glyphs = Array.from(value)
    .map((char) => ({ char, glyph: CLOCK_GLYPH_PATHS[char] }))
    .filter((entry): entry is { char: string; glyph: NonNullable<typeof entry.glyph> } => Boolean(entry.glyph));
  const totalAdvance = glyphs.reduce((sum, entry) => sum + entry.glyph.advance, 0);
  const startX = anchor === "end"
    ? anchorX - totalAdvance
    : anchor === "middle"
      ? anchorX - totalAdvance / 2
      : anchorX;
  let cursor = startX;

  return glyphs.map((entry, index) => {
    const layout = {
      id: `${idPrefix}-${index}-${entry.char}`,
      d: entry.glyph.d,
      x: Number(cursor.toFixed(3))
    };
    cursor += entry.glyph.advance;
    return layout;
  });
}
